import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import { Mail, MapPin, Phone } from 'lucide-react'
import { Logo } from '@/components/generals/logo'

import {
    centers,
    contactEmail,
    contactPhones,
    socialLinks
} from '@/constants/content'

const EXPLORE_LINKS: Array<NavLink> = [
    { label: "Programs & fees", href: '/#programs' },
    { label: "Student portal", href: '/#portal' },
    { label: "Sign in", href: '/#sign-in' },
    { label: "FAQ", href: '/#faq' }
]

const COMPANY_LINKS: Array<NavLink> = [
    { label: "About us", href: '/#about' },
    { label: "Training centers", href: '/#centers' },
    { label: "Contact us", href: '/#contact' },
    { label: "Register", href: '/#register' }
]

const linkClass = 'rounded text-sm text-white/65 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'

const headingClass = 'font-mono text-xs font-medium uppercase tracking-[0.18em] text-white/45'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-ink text-white'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-40 left-1/2 h-80 w-[48rem] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]' />

            <div className='relative mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-20'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1.5fr]'>
                    <div>
                        <Logo tone='light' />

                        <p className='mt-5 max-w-xs text-sm leading-relaxed text-white/60'>
                            Trainer-led IT programs and internships on live software projects, from a full-stack development company serving clients in the USA, UK and India.
                        </p>

                        <ul className='mt-6 flex gap-2'>
                            {socialLinks.map((social) => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={social.label}
                                        className='flex size-10 items-center justify-center rounded-lg border border-white/10 text-white/70 transition-colors hover:border-white/25 hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'
                                    >
                                        <social.icon className='size-4' aria-hidden='true' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <nav aria-label="Explore">
                        <h2 className={headingClass}>
                            Explore
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {EXPLORE_LINKS.map((link) => (
                                <li key={link.href}>
                                    <a href={link.href} className={linkClass}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label="Company">
                        <h2 className={headingClass}>
                            Company
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {COMPANY_LINKS.map((link) => (
                                <li key={link.href}>
                                    <a href={link.href} className={linkClass}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <h2 className={headingClass}>
                            Get in touch
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {contactPhones.map((phone) => (
                                <li key={phone.href}>
                                    <a href={phone.href} className={`${linkClass} inline-flex items-center gap-2.5`}>
                                        <Phone className='size-4 text-teal' aria-hidden='true' />
                                        {phone.display}
                                    </a>
                                </li>
                            ))}

                            <li>
                                <a href={`mailto:${contactEmail}`} className={`${linkClass} inline-flex items-center gap-2.5 break-all`}>
                                    <Mail className='size-4 shrink-0 text-teal' aria-hidden='true' />
                                    {contactEmail}
                                </a>
                            </li>

                            <li className='flex items-start gap-2.5 text-sm text-white/65'>
                                <MapPin className='mt-0.5 size-4 shrink-0 text-teal' aria-hidden='true' />
                                {centers.map((center) => `${center.area}, ${center.city}`).join(" and ")}
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-white/10 pt-8 text-sm text-white/45 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © 2026 Webify IT Technologies Pvt Ltd. All rights reserved.
                    </p>

                    <p>
                        Training centers in Hyderabad and Vijayawada
                    </p>
                </div>
            </div>
        </footer>
    )
}
